import * as React from 'react';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { ButtonType, Pagination, PaginationProps } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { classes } from './Pagination.visual.st.css';
import { PaginationDriver, paginationDriverFactory } from './Pagination.driver';
import { visualize, story, snap } from 'storybook-snapper';
import { VisualAsync } from '../../test/visual/VisualAsync';

interface IPaginationVisualProps extends PaginationProps {
  mobile?: boolean;
}

class PaginationVisual extends React.Component<IPaginationVisualProps> {
  render() {
    const { mobile, rtl } = this.props;

    return (
      <TPAComponentsProvider value={{ mobile, rtl }}>
        <VisualTestContainer>
          <Pagination data-hook="storybook-e2e-LikeButton" {...this.props} />
        </VisualTestContainer>
      </TPAComponentsProvider>
    );
  }
}

const tests = [
  {
    describe: 'basic',
    its: [
      {
        it: 'default',
        props: {
          totalPages: 10,
        },
      },
      {
        it: 'mobile',
        props: {
          mobile: true,
          totalPages: 10,
        },
      },
      {
        it: 'compact mode',
        props: {
          paginationMode: 'compact',
          totalPages: 10,
        },
      },
      {
        it: 'rtl',
        props: {
          rtl: true,
          totalPages: 10,
        },
      },
    ],
  },
  {
    describe: 'style overrides',
    its: [
      {
        it: 'text font',
        props: {
          className: classes.textFont,
          totalPages: 10,
        },
      },
      {
        it: 'nav button size',
        props: {
          className: classes.navButtonSvgSize,
          totalPages: 10,
        },
      },
      {
        it: 'text font color',
        props: {
          className: classes.textFontColor,
          totalPages: 10,
        },
      },
      {
        it: 'selected text font color',
        props: {
          className: classes.selectedTextFontColor,
          totalPages: 10,
        },
      },
      {
        it: 'disabled text font color',
        props: {
          className: classes.disabledTextFontColor,
          totalPages: 10,
        },
      },
      {
        it: 'text opacity',
        props: {
          className: classes.textOpacity,
          totalPages: 10,
        },
      },
      {
        it: 'selected text opacity',
        props: {
          className: classes.selectedTextOpacity,
          totalPages: 10,
        },
      },
      {
        it: 'nav button opacity',
        props: {
          className: classes.navButtonSvgOpacity,
          totalPages: 10,
        },
      },
      {
        it: 'wst text color red',
        props: {
          className: classes.wstTextColor,
          totalPages: 10,
        },
      },
      {
        it: 'wst link color orange',
        props: {
          className: classes.wstLinkColor,
          totalPages: 10,
        },
      },
      {
        it: 'wst font',
        props: {
          className: classes.wstFont,
          totalPages: 10,
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props }) => {
    storiesOf(`Pagination/${describe}`, module).add(it, () => (
      <PaginationVisual {...props} />
    ));
  });
});

visualize('Pagination', () => {
  const pageUrl = (_page) =>
    'https://bo.wix.com/pages/wix-ui-tpa/?path=/story/components-components--pagination';
  const onChange = ({ event, page: _page }) => {
    event.preventDefault();
  };
  const commonProps = { totalPages: 10, onChange, pageUrl };
  const focus = [
    {
      describe: 'focus',
      its: [
        {
          it: 'pages mode',
          props: {
            ...commonProps,
            showFirstPage: true,
            showLastPage: true,
          },
          action: async (driver: PaginationDriver) =>
            (await driver.getPageByNumber(10))
              .getNative()
              .then((el) => el.focus()),
        },
        {
          it: 'input mode',
          props: { ...commonProps, paginationMode: 'input' as const },
          action: async (driver: PaginationDriver) =>
            (await driver.getNavButton(ButtonType.Next))
              .getNative()
              .then((el) => el.focus()),
        },
        {
          it: 'compact mode',
          props: { ...commonProps, paginationMode: 'compact' as const },
          action: async (driver: PaginationDriver) =>
            (await driver.getNavButton(ButtonType.Next))
              .getNative()
              .then((el) => el.focus()),
        },
      ],
    },
  ];

  const withoutLink = focus.map((focusTest) => ({
    ...focusTest,
    describe: `${focusTest.describe} without link`,
    its: focusTest.its.map((it) => ({
      ...it,
      props: { ...it.props, pageUrl: undefined },
    })),
  }));

  [...focus, ...withoutLink].forEach(({ describe, its }) => {
    story(`${describe}`, () => {
      its.forEach(({ it, props, action }) => {
        snap(`${it}`, (done) => (
          <VisualAsync
            driverFactory={paginationDriverFactory}
            onDone={done}
            action={action}
          >
            <Pagination {...props} />
          </VisualAsync>
        ));
      });
    });
  });
});
